/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useEffect, useState} from 'react';
import {Button, Form, Image, Input, Modal, Table, TableColumnsType, Tag} from "antd";
import {CheckOutlined, DeleteOutlined, EditOutlined} from "@ant-design/icons";
import {BaechaList2TableItem, BaechaWorkDayItem, emptyModalData, ModalDataIF} from "../../../BaechaList2.interface";
import {formatKorDate} from "../../../../../util/date";
import {formatCurrency, getBaechaMHTypeStr, getBaechaPriceTypeStr} from "../../../../../util/format";
import {BIG_CAR_TYPES, getModalOkBtnStr, getModalTitle, SMALL_CAR_TYPES} from "../../../BaechaList2Util";
import AddForm from "./ModalContents/AddForm/AddForm";
import EditForm from "./ModalContents/EditForm/EditForm";
import {
	addBaechaWorkDayAPI,
	deleteBaechaWorkDayAPI,
	doneBaechaWorkDayAPI,
	fetchBaechaWorkDayAPI,
	updateBaechaWorkDayAPI,
	updateBaechaWorkDayStatus,
} from "../../../../../api/baechaAPI";
import {useBaechaList2Store} from "../../../../../stores/baechaList2Store";
import {useMessageStore} from "../../../../../stores/messageStore";
import {BaechaWorkDayStatusEnum} from "../../../../../types/Type";

interface TabBaechaWorkDayProps {
	data: BaechaList2TableItem;
}

const TabBaechaWorkDay = ({ data }: TabBaechaWorkDayProps) => {
	const fetch = useBaechaList2Store((s) => s.fetch);
	const setMessages = useMessageStore((s) => s.setMessages);

	const [form] = Form.useForm();

	const [modalData, setModalData] = useState<ModalDataIF>(emptyModalData);
	const [modalTitleStr, setModalTitleStr] = useState('');
	const [workDayListData, setWorkDayListData] = useState<any[]>([]);
	const [rejectReasons, setRejectReasons] = useState<Record<string, string>>({});

	// 수정: 1. 월대면 수정 안 됨. 2. 이미 완료돼있으면 안 됨.
	const getIsShowEditBtn = (baechaWorkDay: BaechaWorkDayItem) => {
		if (data.status === "balju_cancel") return false;
		if (data.isWorkMonth) return false;
		return !baechaWorkDay.doneDate && !baechaWorkDay.doneMemberId;
	};

	// 삭제: 1. 첫번째 일정은 무조건 안 됨. 2. 완료 상태면 안 됨.
	const getIsShowDeleteBtn = (baechaWorkDay: BaechaWorkDayItem) => {
		if (data.status === "balju_cancel") return false;
		if (data.baechaWorkDayList[0].id === baechaWorkDay.id) return false;
		return !baechaWorkDay.doneDate && !baechaWorkDay.doneMemberId;
	}

	// 완료: 1. 이미 완료돼있으면 안 됨. 2. 앞선 일정이 완료되어 있어야 함.
	const getIsShowDoneBtn = (baechaWorkDay: BaechaWorkDayItem) => {
		if (data.status === "balju_cancel") return false;
		if (baechaWorkDay.status) return false;
		if (baechaWorkDay.doneDate || baechaWorkDay.doneMemberId) return false;
		const index = data.baechaWorkDayList.findIndex((item) => item.id === baechaWorkDay.id);
		const earlier = data.baechaWorkDayList.slice(0, index);
		return earlier.every((item) => item.doneDate || item.doneMemberId);
	}

	const getIsShowAddBtn = () => {
		if (data.isWorkMonth) return false;
		if (data.status === 'balju_cancel') return false;
		return !data.baechaWorkDayList.every((item) => item.doneDate || item.doneMemberId);
	};

	const getExtraWorkTag = (status?: BaechaWorkDayStatusEnum) => {
		if (status === BaechaWorkDayStatusEnum.SAVE) return <Tag color="purple">임시저장</Tag>;
		if (status === BaechaWorkDayStatusEnum.REQUEST) return <Tag color="green">승인요청</Tag>;
		if (status === BaechaWorkDayStatusEnum.CANCEL) return <Tag color="red">요청취소</Tag>;
		if (status === BaechaWorkDayStatusEnum.APPROVE) return <Tag color="blue">승인</Tag>;
		if (status === BaechaWorkDayStatusEnum.REJECT) return <Tag color="red">거절</Tag>;
		return null;
	};

	const onApprove = async (baechaId: string, baechaWorkDayId: string) => {
		try {
			await updateBaechaWorkDayStatus({
				baechaId,
				baechaWorkDayId,
				status: BaechaWorkDayStatusEnum.APPROVE,
			});
			setMessages('success', '상태 변경');
			await fetch();
		} catch (e) {
			console.error(e);
			setMessages('error', '오류 발생: 상태 변경 실패');
		}
	};

	const onReject = async (baechaId: string, baechaWorkDayId: string, reason?: string) => {
		try {
			await updateBaechaWorkDayStatus({
				baechaId,
				baechaWorkDayId,
				status: BaechaWorkDayStatusEnum.REJECT,
				reason,
			});
			setMessages('success', '상태 변경');
			setRejectReasons(prev => {
				const next = { ...prev };
				delete next[baechaWorkDayId];
				return next;
			});
			await fetch();
		} catch (e) {
			console.error(e);
			setMessages('error', '오류 발생: 상태 변경 실패');
		}
	};

	const columns: TableColumnsType<BaechaWorkDayItem> = [
		{
			title: '작업 날짜',
			dataIndex: 'workDate',
			key: 'workDate',
			width: 150,
			align: 'center',
			render: (value: any, record: BaechaWorkDayItem) => {
				const date = formatKorDate(value);
				return (
					<div
						style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', cursor: 'pointer'}}
						onClick={(e) => {
							e.stopPropagation();
							navigator.clipboard.writeText(record.id)
								.then(() => setMessages('success', `일일작업 ID 복사: ${record.id}`))
								.catch(() => setMessages('error', '일일작업 ID 복사 실패'));
						}}
					>
						{date}
						{getExtraWorkTag(record.status)}
					</div>
				)
			},
		},
		{
			title: '작업 시간',
			dataIndex: 'mhType',
			key: 'mhType',
			width: 70,
			align: 'center',
			render: (value: string) => getBaechaMHTypeStr(value),
		},
		{
			title: '단가 유형',
			dataIndex: 'priceType',
			key: 'priceType',
			width: 70,
			align: 'center',
			render: (value: string) => getBaechaPriceTypeStr(value),
		},
		{
			title: '단가',
			dataIndex: 'price',
			key: 'price',
			width: 100,
			align: 'center',
			render: (value: string) => formatCurrency(Number(value), 'KRW'),
		},
		{
			title: '야간 연장',
			dataIndex: 'overtimePrice',
			key: 'overtimePrice',
			width: 100,
			align: 'center',
			render: (value: string) => formatCurrency(Number(value), 'KRW'),
		},
		{
			title: (
				<div style={{ display: 'flex', width: '100%' }}>
					<span style={{ flex: 1, textAlign: 'center' }}>수정</span>
					<span style={{ flex: 1, textAlign: 'center' }}>삭제</span>
					<span style={{ flex: 1, textAlign: 'center' }}>완료</span>
				</div>
			),
			key: 'beforeComplete',
			width: 200,
			render: (value: any, record: BaechaWorkDayItem) => {
				const isShowEditBtn = getIsShowEditBtn(record);
				const isShowDelete = getIsShowDeleteBtn(record);
				const isShowDoneBtn = getIsShowDoneBtn(record);
				const isAlreadyDone = record.doneDate || record.doneMemberId;

				const slotStyle = { flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' };

				return (
					<div style={{ display: 'flex', width: '100%' }}>
						<div style={slotStyle}>
							{isShowEditBtn && (
								<Button
									shape='circle'
									type='default'
									onClick={() => {
										setModalData({type: 'EDIT', data, selectedBaechaWorkDayData: record});
										const dayNum = data.baechaWorkDayList.findIndex((item) => item.id === record.id) + 1;
										const modalTitle = getModalTitle('EDIT', dayNum);
										setModalTitleStr(modalTitle);
									}}
								>
									<EditOutlined/>
								</Button>
							)}
						</div>

						<div style={slotStyle}>
							{isShowDelete && (
								<Button
									danger
									shape='circle'
									type='primary'
									onClick={() => {
										setModalData({type: 'DELETE', data, selectedBaechaWorkDayData: record});
										const dayNum = data.baechaWorkDayList.findIndex((item) => item.id === record.id) + 1;
										const modalTitle = getModalTitle('DELETE', dayNum);
										setModalTitleStr(modalTitle);
									}}
								>
									<DeleteOutlined/>
								</Button>
							)}
						</div>

						<div style={slotStyle}>
							{isShowDoneBtn && (
								<Button
									shape='circle'
									type='primary'
									onClick={() => {
										setModalData({type: 'DONE', data, selectedBaechaWorkDayData: record});
										const dayNum = data.baechaWorkDayList.findIndex((item) => item.id === record.id) + 1;
										const modalTitle = getModalTitle('DONE', dayNum);
										setModalTitleStr(modalTitle);
									}}
								>
									<CheckOutlined/>
								</Button>
							)}
							{isAlreadyDone && <span>완료됨</span>}
						</div>
					</div>
				)
			}
		},
		{
			title: '추가작업 사진',
			key: 'photo',
			width: 100,
			align: 'center',
			render: (value: any, record: BaechaWorkDayItem) => {
				if (!record.status) {
					return null;
				}

				const target = workDayListData.find((item) => item.id === record.id);

				if (!target || target.fileInfo?.length === 0) {
					return <span style={{ color: '#ccc' }}>등록된 사진 없음</span>;
				}
				return (
					<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
						{target.fileInfo?.map((file: any, i: number) => (
							<Image
								key={i}
								src={file.url}
								alt={file.name || `photo-${i}`}
								width={100}
								style={{
									maxWidth: '100%',
									borderRadius: 8,
								}}
							/>
						))}
					</div>
				);
			}
		},
		{
			title: '승인 / 거절',
			key: 'apporve/reject',
			width: 100,
			align: 'center',
			render: (value, record) => {
				const currentReason = rejectReasons[record.id] || '';

				return (
					<>
						{record.reason && <div>거절사유: {record.reason}</div>}

						{(record.status === BaechaWorkDayStatusEnum.REQUEST) && (
							<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: '16px' }}>
								<Button
									type="primary"
									onClick={() => onApprove(data.id, record.id)}
								>
									승인
								</Button>
								<div style={{ display: 'flex', gap: '8px' }}>
									<Input
										placeholder="거절 사유"
										style={{ width: '150px'}}
										value={currentReason}
										onChange={(e) => {
											setRejectReasons(prev => ({ ...prev, [record.id]: e.target.value }));
										}}
									/>
									<Button
										color="danger"
										variant="solid"
										onClick={() => onReject(data.id, record.id, currentReason)}
									>
										거절
									</Button>
								</div>
							</div>
						)}
					</>
				)
			},
		}
	];

	const renderModalContent = () => {
		switch (modalData.type) {
			case 'ADD':
				return <AddForm form={form} modalData={modalData} />;
			case 'EDIT':
				return <EditForm form={form} modalData={modalData} />;
			case 'DELETE':
			case 'DONE':
			default:
				return null;
		}
	};

	const onOkAdd = async (values: any) => {
		const { price, mhType, overtimePrice, workDate } = values;
		const { carType, id: baechaId } = data;
		const isSmallCar = !!carType && SMALL_CAR_TYPES.has(carType);
		const isBigCar = !!carType && BIG_CAR_TYPES.has(carType);

		await addBaechaWorkDayAPI({
			workDate,
			baechaId,
			mhType,
			price: isSmallCar
				? price
				: isBigCar
					? price * 10_000
					: 0,
			overtimePrice: overtimePrice * 10_000 || 0,
		});
	};

	const isChanged = (a: any, b: any) => (a ?? null) !== (b ?? null);

	const normalizePrice = (price: number, isBigCar: boolean) => isBigCar ? price * 10_000 : price;

	const normalizeOvertime = (price: number) => price * 10_000;

	const onOkEdit = async (values: any) => {
		const origin = modalData.selectedBaechaWorkDayData;
		const baechaWorkDayId = origin?.id;

		if (!baechaWorkDayId) {
			throw new Error('오류가 발생했습니다.');
		}

		const { price, mhType, overtimePrice, workDate } = values;
		const { carType, id: baechaId } = data;

		const isBigCar = !!carType && BIG_CAR_TYPES.has(carType);

		const next = {
			workDate,
			mhType,
			price: normalizePrice(price ?? 0, isBigCar),
			overtimePrice: normalizeOvertime(overtimePrice ?? 0),
		};

		const prev = {
			workDate: origin.workDate,
			mhType: origin.mhType,
			price: origin.price ?? 0,
			overtimePrice: origin.overtimePrice ?? 0,
		};

		const payload = Object.fromEntries(
			Object.entries(next).filter(([key, value]) => {
				return isChanged(value, (prev as any)[key]);
			})
		);

		await updateBaechaWorkDayAPI({
			baechaId,
			baechaWorkDayId,
			...payload,
		});
	};

	const onOkDelete = async () => {
		if (!modalData.selectedBaechaWorkDayData) {
			throw new Error('오류가 발생했습니다.');
		};

		const { id: baechaId } = data;
		await deleteBaechaWorkDayAPI({
			baechaId,
			baechaWorkDayId: modalData.selectedBaechaWorkDayData.id,
		});
	};

	const onOkDone = async () => {
		if (!modalData.selectedBaechaWorkDayData) {
			throw new Error('오류가 발생했습니다.');
		};

		const { id: baechaId } = data;
		await doneBaechaWorkDayAPI({
			baechaId,
			baechaWorkDayId: modalData.selectedBaechaWorkDayData.id,
			doneMemberId: 'SYSTEM',
		});
	};

	useEffect(() => {
		if (data?.id) {
			fetchBaechaWorkDayAPI(data.id)
				.then((res) => setWorkDayListData(res))
				.catch((err) => console.error(err))
		}
	}, [data]);

	return (
		<>
			<Table
				bordered
				size='small'
				style={{ tableLayout: 'fixed', width: '100%' }}
				scroll={{ x: 'max-content' }}
				pagination={false}
				rowHoverable={false}
				dataSource={data.baechaWorkDayList.map((row) => ({...row, key: row.id}))}
				columns={columns}
				onRow={(record) => {
					return {
						style: (record.status) ? {backgroundColor: '#E0F2FF'} : {},
					}
				}}
			/>
			{getIsShowAddBtn() && (
				<div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<Button
						onClick={() => {
							setModalData({ type: 'ADD', data, selectedBaechaWorkDayData: undefined });
							const dayNum = data.baechaWorkDayList.length + 1;
							const modalTitle = getModalTitle('ADD', dayNum);
							setModalTitleStr(modalTitle);
						}}
					>
						추가
					</Button>
				</div>
			)}

			<Modal
				open={!!modalData.type}
				onCancel={() => {
					form.resetFields();
					setModalData({ type: undefined, data: undefined, selectedBaechaWorkDayData: undefined })
				}}
				onOk={async () => {
					try {
						const values = await form.validateFields();
						if (modalData.type === 'ADD') {
							await onOkAdd(values);
						}
						if (modalData.type === 'EDIT') {
							await onOkEdit(values);
						}
						if (modalData.type === 'DELETE') {
							await onOkDelete();
						}
						if (modalData.type === 'DONE') {
							await onOkDone();
						}
						await fetch();
						setMessages('success', `${modalTitleStr} 성공`);
					} catch (e) {
						setMessages('error', `${modalTitleStr} 중 오류가 발생했습니다.`);
					} finally {
						form.resetFields();
						setModalData({ type: undefined, data: undefined, selectedBaechaWorkDayData: undefined });
					}
				}}
				title={modalTitleStr}
				destroyOnClose
				style={{ top: 10 }}
				okText={getModalOkBtnStr(modalData.type)}
			>
				{renderModalContent()}
			</Modal>
		</>
	);
};

export default TabBaechaWorkDay;
